/**
 * ══════════════════════════════════════════════════════════════════════════════
 * FREE SHIPPING PROGRESS BAR - MULTI-TIER REWARDS
 * Version: 5.2 (3-Tier Progress: Shipping → Gift → Discount)
 * ══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  "use strict";

  if (window.__FSB_INIT__) return;
  window.__FSB_INIT__ = true;

  const CONFIG = {
    debug: false, // Set to true for troubleshooting
    routes: {
      cart: '/cart.js',
      cartAdd: '/cart/add.js',
      cartUpdate: '/cart/update.js'
    },
    sectionIds: ['cart-drawer', 'cart-notification', 'cart-icon-bubble']
  };

  const STATE = {
    isProcessing: false,
    discountBusy: false,
    giftShown: false,
    timerInterval: null,
    timerEndTime: null,
    currentTier: 0,
    sparkled: {},
    toastTimeout: null
  };

  function log(...args) {
    if (CONFIG.debug) console.log('[FSB]', ...args);
  }

  function logError(...args) {
    console.error('[FSB ERROR]', ...args);
  }

  async function fetchCart() {
    try {
      const response = await fetch(CONFIG.routes.cart, { credentials: 'same-origin' });
      const cart = await response.json();
      log('Cart fetched:', {
        subtotal: cart.items_subtotal_price,
        total: cart.total_price,
        discounts: cart.cart_level_discount_applications,
        itemCount: cart.item_count
      });
      return cart;
    } catch (e) {
      logError('Failed to fetch cart:', e);
      return null;
    }
  }

  function formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100);
    
    if (!format) format = '₹{{amount}}';
    
    return format
      .replace('{{amount}}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  function getTierConfig(root) {
    return {
      tier1: {
        enabled: root.dataset.tier1Enabled === 'true',
        threshold: Math.round(parseFloat(root.dataset.tier1Threshold || 0) * 100),
        thresholdRupees: parseFloat(root.dataset.tier1Threshold || 0),
        label: root.dataset.tier1Label || 'Free Shipping',
        icon: root.dataset.tier1Icon || '🚚',
        progressMsg: root.dataset.tier1Progress || 'Add {{amount}} for FREE SHIPPING',
        successMsg: root.dataset.tier1Success || '🎉 FREE SHIPPING Unlocked!'
      },
      tier2: {
        enabled: root.dataset.tier2Enabled === 'true',
        threshold: Math.round(parseFloat(root.dataset.tier2Threshold || 0) * 100),
        thresholdRupees: parseFloat(root.dataset.tier2Threshold || 0),
        label: root.dataset.tier2Label || 'Free Gift',
        icon: root.dataset.tier2Icon || '🎁',
        progressMsg: root.dataset.tier2Progress || 'Add {{amount}} for FREE GIFT',
        successMsg: root.dataset.tier2Success || '🎁 FREE GIFT Unlocked!',
        giftVariant: root.dataset.giftVariant,
        giftTitle: root.dataset.giftTitle,
        giftImg: root.dataset.giftImg,
        giftCompare: root.dataset.giftCompare,
        giftHeadline: root.dataset.giftHeadline || '🎁 You unlocked a FREE GIFT!',
        giftBtn: root.dataset.giftBtn || 'CLAIM FREE GIFT'
      },
      tier3: {
        enabled: root.dataset.tier3Enabled === 'true',
        threshold: Math.round(parseFloat(root.dataset.tier3Threshold || 0) * 100),
        thresholdRupees: parseFloat(root.dataset.tier3Threshold || 0),
        label: root.dataset.tier3Label || 'Discount',
        icon: root.dataset.tier3Icon || '💰',
        progressMsg: root.dataset.tier3Progress || 'Add {{amount}} for {{discount}} OFF',
        successMsg: root.dataset.tier3Success || '💰 {{discount}} Discount Applied!',
        discountCode: root.dataset.discountCode || '',
        discountPercent: root.dataset.discountPercent || '10%'
      }
    };
  }

  function calculateTierStatus(subtotalCents, tierConfig) {
    const tiers = [];
    let activeTiers = 0;
    let highestCompletedTier = 0;
    
    if (tierConfig.tier1.enabled && tierConfig.tier1.threshold > 0) {
      const completed = subtotalCents >= tierConfig.tier1.threshold;
      const progress = Math.min(100, (subtotalCents / tierConfig.tier1.threshold) * 100);
      const remaining = Math.max(0, tierConfig.tier1.threshold - subtotalCents);
      tiers.push({ tier: 1, ...tierConfig.tier1, completed, progress, remaining });
      activeTiers++;
      if (completed) highestCompletedTier = 1;
    }
    
    if (tierConfig.tier2.enabled && tierConfig.tier2.threshold > 0) {
      const completed = subtotalCents >= tierConfig.tier2.threshold;
      const prevThreshold = tierConfig.tier1.enabled ? tierConfig.tier1.threshold : 0;
      const tierRange = tierConfig.tier2.threshold - prevThreshold;
      const tierProgress = subtotalCents - prevThreshold;
      const progress = tierRange > 0 ? Math.min(100, Math.max(0, (tierProgress / tierRange) * 100)) : (completed ? 100 : 0);
      const remaining = Math.max(0, tierConfig.tier2.threshold - subtotalCents);
      tiers.push({ tier: 2, ...tierConfig.tier2, completed, progress, remaining });
      activeTiers++;
      if (completed) highestCompletedTier = 2;
    }
    
    if (tierConfig.tier3.enabled && tierConfig.tier3.threshold > 0) {
      const completed = subtotalCents >= tierConfig.tier3.threshold;
      const prevThreshold = tierConfig.tier2.enabled ? tierConfig.tier2.threshold : 
                           (tierConfig.tier1.enabled ? tierConfig.tier1.threshold : 0);
      const tierRange = tierConfig.tier3.threshold - prevThreshold;
      const tierProgress = subtotalCents - prevThreshold;
      const progress = tierRange > 0 ? Math.min(100, Math.max(0, (tierProgress / tierRange) * 100)) : (completed ? 100 : 0);
      const remaining = Math.max(0, tierConfig.tier3.threshold - subtotalCents);
      tiers.push({ tier: 3, ...tierConfig.tier3, completed, progress, remaining });
      activeTiers++;
      if (completed) highestCompletedTier = 3;
    }
    
    STATE.currentTier = highestCompletedTier;
    const maxThreshold = tiers.length > 0 ? tiers[tiers.length - 1].threshold : 0;
    const overallProgress = maxThreshold > 0 ? Math.min(100, (subtotalCents / maxThreshold) * 100) : 0;
    
    return { tiers, activeTiers, overallProgress, subtotalCents, currentTier: highestCompletedTier };
  }

  async function updateProgressBar(root, cart) {
    if (!cart || !root) return;
    
    const subtotalCents = cart.items_subtotal_price;
    const tierConfig = getTierConfig(root);
    const status = calculateTierStatus(subtotalCents, tierConfig);
    
    log('Tier Status:', status);
    
    updateProgressBarUI(root, status);
    updateProgressMessage(root, status);
    await handleTierActions(root, status, tierConfig, cart);
  }

  function updateProgressBarUI(root, status) {
    const progressContainer = root.querySelector('[data-fs-progress-bar]');
    if (!progressContainer) return;
    
    let segmentsContainer = progressContainer.querySelector('.fs-tier-segments');
    if (!segmentsContainer) {
      segmentsContainer = document.createElement('div');
      segmentsContainer.className = 'fs-tier-segments';
      progressContainer.innerHTML = '';
      progressContainer.appendChild(segmentsContainer);
    }
    
    let segmentsHTML = '';
    const tierWidth = status.activeTiers > 0 ? (100 / status.activeTiers) : 100;
    
    status.tiers.forEach((tier, index) => {
      const completedClass = tier.completed ? 'fs-tier-segment--completed' : '';
      const activeClass = (!tier.completed && (index === 0 || status.tiers[index - 1]?.completed)) ? 'fs-tier-segment--active' : '';
      const fillWidth = tier.completed ? 100 : Math.round(tier.progress);
      
      segmentsHTML += `
        <div class="fs-tier-segment ${completedClass} ${activeClass}" style="width: ${tierWidth}%;" data-tier="${tier.tier}">
          <div class="fs-tier-fill" style="width: ${fillWidth}%;"></div>
          <div class="fs-tier-marker">
            <span class="fs-tier-icon">${tier.completed ? '✓' : tier.icon}</span>
            <div class="fs-tier-info">
              <span class="fs-tier-label">${tier.label}</span>
              <span class="fs-tier-threshold">₹${tier.thresholdRupees.toLocaleString()}</span>
            </div>
          </div>
        </div>
      `;
    });
    
    segmentsContainer.innerHTML = segmentsHTML;
    
    status.tiers.forEach(tier => {
      if (tier.completed && !STATE.sparkled[tier.tier]) {
        STATE.sparkled[tier.tier] = true;
        triggerSparkles(root, tier.tier);
      }
      if (!tier.completed && STATE.sparkled[tier.tier]) {
        STATE.sparkled[tier.tier] = false;
      }
    });
  }

  function updateProgressMessage(root, status) {
    const messageEl = root.querySelector('[data-fs-message]');
    if (!messageEl) return;
    
    const nextTier = status.tiers.find(t => !t.completed);
    const allComplete = status.tiers.every(t => t.completed);
    
    let message = '';
    let messageClass = '';
    
    if (allComplete && status.tiers.length > 0) {
      message = '🎉 Congrats! All rewards unlocked!';
      messageClass = 'fs-message--complete';
    } else if (nextTier) {
      const remaining = formatMoney(nextTier.remaining, '₹{{amount_no_decimals}}');
      message = nextTier.progressMsg.replace('{{amount}}', remaining).replace('{{discount}}', nextTier.discountPercent || '');
      messageClass = 'fs-message--progress';
      
      const completedTiers = status.tiers.filter(t => t.completed);
      if (completedTiers.length > 0) {
        const summary = completedTiers.map(t => `${t.icon} ${t.label}`).join(' ');
        message = `<span class="fs-completed-summary">✓ ${summary}</span> ${message}`;
      }
    } else {
      message = 'Add items to your cart!';
    }
    
    messageEl.innerHTML = message;
    messageEl.className = `fs-message ${messageClass}`;
  }

  async function handleTierActions(root, status, tierConfig, cart) {
    const tier2 = status.tiers.find(t => t.tier === 2);
    
    if (tier2?.completed && !STATE.giftShown && tierConfig.tier2.giftVariant) {
      log('>>> TRIGGERING GIFT POPUP');
      openGiftModal(root, tierConfig.tier2);
    }
    
    if (tier2 && !tier2.completed && STATE.giftShown) {
      STATE.giftShown = false;
    }
    
    const tier3 = status.tiers.find(t => t.tier === 3);
    if (tier3 && tierConfig.tier3.discountCode) {
      const discountApplied = isDiscountApplied(cart, tierConfig.tier3.discountCode);
      
      log('Discount Check:', { tier3Completed: tier3.completed, discountApplied, code: tierConfig.tier3.discountCode });
      
      if (tier3.completed && !discountApplied) {
        log('>>> TRIGGERING APPLY DISCOUNT');
        await applyDiscount(tierConfig.tier3.discountCode);
      } else if (!tier3.completed && discountApplied) {
        log('>>> TRIGGERING REMOVE DISCOUNT');
        await removeDiscount(tierConfig.tier3.discountCode);
      }
    }
  }

  function triggerSparkles(root, tierNum) {
    const segment = root.querySelector(`[data-tier="${tierNum}"]`);
    if (!segment) return;
    
    let box = segment.querySelector('.fs-sparkles');
    if (!box) {
      box = document.createElement('div');
      box.className = 'fs-sparkles';
      segment.appendChild(box);
    }
    
    box.innerHTML = '';
    const colors = ['#FFD54A', '#00C46A', '#4FC3F7', '#FF6B6B', '#B39DDB', '#FF9800'];
    
    for (let i = 0; i < 15; i++) {
      const el = document.createElement('i');
      el.style.cssText = `--x:${Math.random()*100}%;--dx:${(Math.random()-0.5)*100}px;--dy:${-(30+Math.random()*50)}px;--r:${Math.random()*360}deg;--c:${colors[Math.floor(Math.random()*colors.length)]};`;
      box.appendChild(el);
    }
    setTimeout(() => { box.innerHTML = ''; }, 1100);
  }

  function isDiscountApplied(cart, code) {
    if (!cart || !code) return false;
    code = code.toUpperCase();
    
    const inDiscountCodes = cart.discount_codes?.some(d => d.code?.toUpperCase() === code);
    const inCartLevel = cart.cart_level_discount_applications?.some(d => 
      d.title?.toUpperCase() === code || d.key?.toUpperCase() === code
    );
    
    return inDiscountCodes || inCartLevel;
  }

async function applyDiscount(code) {
  if (STATE.discountBusy || !code) return false;
  
  STATE.discountBusy = true;
  const isCartPage = window.location.pathname.includes('/cart');
    log('=== APPLYING DISCOUNT ===', code);
    
    try {
      let cart = await fetchCart();
      if (isDiscountApplied(cart, code)) {
        log('Discount applied successfully');
        showToast('Discount applied!', 'success', isCartPage);
        await refreshAfterDiscount();
        STATE.discountBusy = false;
        return true;
      }
      
      showToast('Applying discount...', 'info');
      
      const response = await fetch(CONFIG.routes.cartUpdate, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code })
      });
      
      if (response.ok) {
        await new Promise(r => setTimeout(r, 500));
        cart = await fetchCart();
        
        if (isDiscountApplied(cart, code)) {
          log('Discount applied via URL');
          showToast('Discount applied!', 'success', isCartPage);
          await refreshAfterDiscount();
          STATE.discountBusy = false;
          return true;
        }
      }
      
      // Fallback: URL method
      await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', redirect: 'manual' });
      await new Promise(r => setTimeout(r, 500));
      cart = await fetchCart();
      
      if (!isDiscountApplied(cart, code)) {
        log('Discount removed successfully');
        showToast('Discount removed - add more to unlock!', 'error', isCartPage);
        await refreshAfterDiscount();
        STATE.discountBusy = false;
        return true;
      }
      
      log('Discount apply failed');
      STATE.discountBusy = false;
      return false;
      
    } catch (e) {
      logError('Apply discount failed:', e);
      STATE.discountBusy = false;
      return false;
    }
  }

  async function removeDiscount(code) {
    if (STATE.discountBusy || !code) return false;
    STATE.discountBusy = true;
    const isCartPage = window.location.pathname.includes('/cart');
    log('=== REMOVING DISCOUNT ===', code);
    
    try {
      let cart = await fetchCart();
      if (!isDiscountApplied(cart, code)) {
        log('Discount not applied');
        STATE.discountBusy = false;
        return true;
      }
      
      showToast('Updating cart...', 'info');
      
      const response = await fetch(CONFIG.routes.cartUpdate, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      });
      
      if (response.ok) {
        document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        await new Promise(r => setTimeout(r, 500));
        cart = await fetchCart();
        
        if (!isDiscountApplied(cart, code)) {
          log('Discount removed successfully');
          showToast('Discount removed - add more to unlock!', 'error', isCartPage);
          STATE.discountBusy = false;
          await refreshAfterDiscount();
          return true;
        }
      }
      
      log('Discount remove failed');
      STATE.discountBusy = false;
      return false;
      
    } catch (e) {
      logError('Remove discount failed:', e);
      STATE.discountBusy = false;
      return false;
    }
  }

  async function refreshAfterDiscount() {
    if (window.location.pathname.includes('/cart')) {
      log('Reloading cart page...');
      setTimeout(() => window.location.reload(), 300);
    } else {
      await dispatchCartEvents();
    }
  }

  async function dispatchCartEvents() {
    log('Dispatching cart events...');
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'fsb' });
    }
    
    await refreshDawnCartDrawer();
  }

  async function refreshDawnCartDrawer() {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return false;
      
      log('Refreshing Dawn cart-drawer...');
      
      const wasOpen = cartDrawer.classList.contains('is-open') || cartDrawer.hasAttribute('open');
      
      await new Promise(r => setTimeout(r, 200));
      
      const response = await fetch(`/cart?sections=cart-drawer&t=${Date.now()}`);
      if (!response.ok) return false;
      
      const data = await response.json();
      const html = data['cart-drawer'];
      if (!html) return false;
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('cart-drawer');
      
      if (newDrawer) {
        cartDrawer.innerHTML = newDrawer.innerHTML;
        
        if (wasOpen) {
          cartDrawer.classList.add('is-open');
          cartDrawer.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        }
        
        log('Cart drawer refreshed');
        
        setTimeout(async () => {
          const cart = await fetchCart();
          const bars = document.querySelectorAll('.fs-progress[data-tier1-threshold]');
          for (const root of bars) {
            const tierConfig = getTierConfig(root);
            const status = calculateTierStatus(cart.items_subtotal_price, tierConfig);
            updateProgressBarUI(root, status);
            updateProgressMessage(root, status);
          }
        }, 100);
        
        return true;
      }
      return false;
    } catch (e) {
      logError('Cart drawer refresh failed:', e);
      return false;
    }
  }

  function initGlobalElements() {
    const modal = document.querySelector('[data-fs-gift-modal]');
    if (modal && modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    
    // Remove all toasts, keep only one in body
    const toasts = document.querySelectorAll('[data-fs-toast]');
    let mainToast = null;
    toasts.forEach((toast, index) => {
      if (index === 0) {
        mainToast = toast;
        if (toast.parentElement !== document.body) {
          document.body.appendChild(toast);
        }
      } else {
        toast.remove(); // Remove duplicates
      }
    });
  }

  function openGiftModal(root, tier2Config) {
    const modal = document.querySelector('[data-fs-gift-modal]');
    if (!modal || STATE.giftShown) return;
    
    const headline = modal.querySelector('[data-fs-gift-headline]');
    const img = modal.querySelector('[data-fs-gift-img]');
    const title = modal.querySelector('[data-fs-gift-title]');
    const compare = modal.querySelector('[data-fs-gift-compare]');
    const btn = modal.querySelector('[data-fs-gift-btn]');
    const note = modal.querySelector('[data-fs-gift-note]');
    
    if (headline) headline.textContent = tier2Config.giftHeadline;
    if (title) title.textContent = tier2Config.giftTitle;
    if (compare) compare.textContent = tier2Config.giftCompare;
    if (img) { img.src = tier2Config.giftImg; img.alt = tier2Config.giftTitle; }
    if (note) { note.hidden = true; note.textContent = ''; }
    if (btn) { btn.disabled = false; btn.textContent = tier2Config.giftBtn; }
    
    modal.hidden = false;
    modal.style.zIndex = '999999';
    STATE.giftShown = true;
    
    modal.querySelectorAll('[data-fs-gift-close]').forEach(el => {
      el.onclick = () => { modal.hidden = true; };
    });
    
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        modal.hidden = true;
        document.removeEventListener('keydown', escHandler);
      }
    });
    
    if (btn) {
      btn.onclick = () => addGiftToCart(tier2Config, modal, btn, note);
    }
  }

  async function addGiftToCart(tier2Config, modal, btn, note) {
    const variantId = parseInt(tier2Config.giftVariant);
    const originalText = btn.textContent;
    
    try {
      btn.disabled = true;
      btn.textContent = 'Adding...';
      
      await fetch(CONFIG.routes.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { '_free_gift': 'true' } }]
        })
      });
      
      btn.textContent = 'Added! ✓';
      showToast('🎁 Free gift added!', 'success');
      
      setTimeout(() => {
        modal.hidden = true;
        handleCartUpdate();
        dispatchCartEvents();
      }, 800);
      
    } catch (e) {
      btn.disabled = false;
      btn.textContent = originalText;
      if (note) { note.hidden = false; note.textContent = 'Failed. Try again.'; }
    }
  }

 function showToast(message, type = 'success', persist = false) {
  // Always clear previous timeout FIRST
  if (STATE.toastTimeout) {
    clearTimeout(STATE.toastTimeout);
    STATE.toastTimeout = null;
  }
  
  // Find toast that's direct child of body, or any toast
  let toast = document.body.querySelector(':scope > .fs-toast') || document.querySelector('[data-fs-toast]');
  
  if (persist) {
    if (toast) toast.hidden = true;
    sessionStorage.setItem('fsb_pending_toast', JSON.stringify({ message, type }));
    return;
  }
  
  if (!toast) return;
  
  // Always move to body (in case it's inside drawer)
  if (toast.parentElement !== document.body) {
    document.body.appendChild(toast);
  }
  
  const icon = toast.querySelector('[data-fs-toast-icon]');
  const msg = toast.querySelector('[data-fs-toast-msg]');
  
  if (icon) icon.textContent = type === 'success' ? '✓' : type === 'error' ? '✕' : '⏳';
  if (msg) msg.textContent = message;
  
  toast.className = 'fs-toast';
  if (type === 'error') toast.classList.add('fs-toast--error');
  if (type === 'info') toast.classList.add('fs-toast--info');
  
  toast.hidden = false;
  
  const duration = 3000;
  
  STATE.toastTimeout = setTimeout(() => { 
    toast.hidden = true; 
    STATE.toastTimeout = null;
  }, duration);
}

  function checkPendingToast() {
    const pending = sessionStorage.getItem('fsb_pending_toast');
    if (pending) {
      sessionStorage.removeItem('fsb_pending_toast');
      try {
        const { message, type } = JSON.parse(pending);
        setTimeout(() => showToast(message, type), 500);
      } catch (e) {}
    }
  }

  function initTimer(root) {
    const timerEnabled = root.dataset.timerEnabled === 'true';
    if (!timerEnabled) return;
    
    const timerEl = root.querySelector('[data-fs-timer]');
    const displayEl = root.querySelector('[data-fs-timer-display]');
    if (!timerEl || !displayEl) return;
    
    const minutes = parseInt(root.dataset.timerMinutes) || 15;
    const storageKey = 'fsb_timer_end';
    const expiredText = root.dataset.timerExpired || 'Expired!';
    
    let endTime = localStorage.getItem(storageKey);
    if (!endTime || parseInt(endTime) < Date.now()) {
      endTime = Date.now() + minutes * 60 * 1000;
      localStorage.setItem(storageKey, endTime);
    }
    STATE.timerEndTime = parseInt(endTime);
    
    if (STATE.timerInterval) clearInterval(STATE.timerInterval);
    
    function updateTimer() {
      const remaining = Math.max(0, STATE.timerEndTime - Date.now());
      if (remaining <= 0) {
        displayEl.textContent = expiredText;
        timerEl.classList.add('fs-timer--warning');
        clearInterval(STATE.timerInterval);
        localStorage.removeItem(storageKey);
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      displayEl.textContent = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      if (remaining < 120000) timerEl.classList.add('fs-timer--warning');
    }
    
    updateTimer();
    STATE.timerInterval = setInterval(updateTimer, 1000);
  }

  async function initRecommendations() {
    const container = document.querySelector('[data-fs-recommendations]');
    if (!container) return;
    
    const productId = container.dataset.productId;
    const limit = parseInt(container.dataset.limit) || 4;
    
    if (!productId || productId === 'null') {
      container.closest('.fs-recs')?.remove();
      return;
    }
    
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`);
      const data = await response.json();
      
      if (data.products?.length > 0) {
        renderRecommendations(container, data.products);
      } else {
        container.closest('.fs-recs')?.remove();
      }
    } catch (e) {
      container.closest('.fs-recs')?.remove();
    }
  }

  function renderRecommendations(container, products) {
    container.innerHTML = products.map(p => `
      <a href="${p.url}" class="fs-rec-item">
        <img src="${p.featured_image || p.images?.[0]}" alt="${p.title}" loading="lazy">
        <span class="fs-rec-title">${p.title}</span>
        <span class="fs-rec-price">${formatMoney(p.price, window.Shopify?.money_format)}</span>
      </a>
    `).join('');
  }

  async function handleCartUpdate() {
    if (STATE.isProcessing) return;
    
    STATE.isProcessing = true;
    log('=== handleCartUpdate START ===');
    
    try {
      const cart = await fetchCart();
      const bars = document.querySelectorAll('.fs-progress[data-tier1-threshold], .fs-progress[data-threshold]');
      
      for (const root of bars) {
        await updateProgressBar(root, cart);
      }
      
      initRecommendations();
    } catch (e) {
      logError('handleCartUpdate error:', e);
    } finally {
      STATE.isProcessing = false;
      log('=== handleCartUpdate END ===');
    }
  }

  let updateTimer = null;
  function scheduleCartUpdate(delay = 300) {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(handleCartUpdate, delay);
  }

  function interceptCartAPI() {
    const originalFetch = window.fetch;
    
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const isCartModify = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
      
      if (isCartModify && !STATE.discountBusy) {
        return originalFetch.apply(this, args).then(res => {
          log('Intercepted cart call:', url);
          scheduleCartUpdate(400);
          return res;
        });
      }
      
      return originalFetch.apply(this, args);
    };
    
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._fsbUrl = url;
      return origOpen.apply(this, arguments);
    };
    
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (/\/cart\/(add|change|update|clear)/.test(this._fsbUrl) && !STATE.discountBusy) {
        this.addEventListener('load', () => {
          scheduleCartUpdate(400);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  function init() {
    log('FSB v5.2 Multi-Tier Initializing...');
    
    interceptCartAPI();
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initGlobalElements();
        checkPendingToast();
        handleCartUpdate();
        initRecommendations();
        const root = document.querySelector('.fs-progress[data-tier1-threshold]');
        if (root) initTimer(root);
      });
    } else {
      initGlobalElements();
      checkPendingToast();
      handleCartUpdate();
      initRecommendations();
      const root = document.querySelector('.fs-progress[data-tier1-threshold]');
      if (root) initTimer(root);
    }
    
    document.addEventListener('click', (e) => {
      const qtySelectors = [
        '[name="plus"]', '[name="minus"]',
        '.qty-plus', '.qty-minus',
        '.quantity__button',
        '[data-quantity-plus]', '[data-quantity-minus]',
        '.cart__qty-button',
        '.ajaxcart__qty-adjust',
        '.js-qty__adjust',
        'button[data-action="decrease-quantity"]',
        'button[data-action="increase-quantity"]',
        '.quantity-selector__button',
        '.product-form__quantity-button'
      ];
      
      if (qtySelectors.some(sel => e.target?.matches?.(sel) || e.target?.closest?.(sel))) {
        scheduleCartUpdate(600);
      }
    }, true);

    document.addEventListener('change', (e) => {
      const inputSelectors = [
        'input[name="updates[]"]',
        'input.quantity__input',
        'input.cart__qty-input',
        'input[data-quantity-input]',
        '.ajaxcart__qty-num',
        'input[type="number"][name^="updates"]'
      ];
      
      if (inputSelectors.some(sel => e.target?.matches?.(sel))) {
        scheduleCartUpdate(500);
      }
    }, true);

    if (window.location.pathname.includes('/cart')) {
      document.querySelectorAll('form[action="/cart"], form[action="/cart/update"]').forEach(form => {
        form.addEventListener('submit', () => {
          scheduleCartUpdate(800);
        });
      });
    }
    
    ['cart:updated', 'cart:refresh', 'cart:change'].forEach(evt => {
      document.addEventListener(evt, () => {
        if (!STATE.discountBusy) {
          scheduleCartUpdate(400);
        }
      });
    });
    
    log('FSB v5.2 Multi-Tier Initialized');
  }

  init();

  window.FSB = {
    handleCartUpdate,
    fetchCart,
    applyDiscount,
    removeDiscount,
    getState: () => STATE,
    version: '5.2'
  };

})();
